/**
 * NotificationsBell — notification icon with unread count badge.
 *
 * Polls GET /v1/collab/invites?unread=true every 30 s to keep the
 * badge count fresh. Clicking the bell opens a popover with the 10
 * most-recent invites. Each invite shows a "Join" button that navigates
 * to /collab/:roomId (marking the invite as read via navigation state).
 *
 * P1-62 — Phase 3C scoped subset of the full notification inbox (P1-58).
 * Currently shows only meeting_invite kinds; other notification types land
 * when the full notifications module is wired (P1-57).
 */

import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { deleteInvite, getInvites, markInviteRead } from '../lib/collabApi';
import type { CollabInvite } from '../lib/collabApi';

const POLL_INTERVAL_MS = 30_000;

export function NotificationsBell() {
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [invites, setInvites] = useState<CollabInvite[]>([]);
  const [open, setOpen] = useState(false);
  const [loadingPopover, setLoadingPopover] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // ── Poll for unread count ─────────────────────────────────────────────

  const refreshBadge = useCallback(async () => {
    try {
      const unread = await getInvites({ unreadOnly: true, limit: 50 });
      setUnreadCount(unread.length);
    } catch (e) {
      // Log to console so a misconfigured token / 401 doesn't silently kill
      // the bell. Don't crash the header on transient 5xx.
      // eslint-disable-next-line no-console
      console.warn('[NotificationsBell] failed to refresh:', e instanceof Error ? e.message : e);
    }
  }, []);

  useEffect(() => {
    void refreshBadge();
    const timer = setInterval(() => void refreshBadge(), POLL_INTERVAL_MS);

    // Refresh immediately when the tab regains focus — so the user doesn't
    // have to wait up to 30s after switching back from another tab.
    function onVisibility() {
      if (document.visibilityState === 'visible') void refreshBadge();
    }
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [refreshBadge]);

  // ── Open popover → load full list ─────────────────────────────────────

  const openPopover = useCallback(async () => {
    setOpen(true);
    setLoadingPopover(true);
    try {
      const list = await getInvites({ limit: 10 });
      setInvites(list);
    } catch {
      setInvites([]);
    } finally {
      setLoadingPopover(false);
    }
  }, []);

  function togglePopover() {
    if (open) setOpen(false);
    else void openPopover();
  }

  // ── Close on outside click ────────────────────────────────────────────

  useEffect(() => {
    if (!open) return;
    function onClickOutside(e: MouseEvent) {
      if (
        popoverRef.current && !popoverRef.current.contains(e.target as Node) &&
        buttonRef.current && !buttonRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  // ── Dismiss / delete invite ───────────────────────────────────────────

  function onDismiss(invite: CollabInvite, ev: React.MouseEvent) {
    ev.stopPropagation();
    // Optimistic remove from local list + drop badge if it was unread
    setInvites((prev) => prev.filter((i) => i.id !== invite.id));
    if (!invite.readAt) setUnreadCount((c) => Math.max(0, c - 1));
    deleteInvite(invite.id).catch(() => {
      // Reverse optimistic update on failure
      setInvites((prev) => [invite, ...prev]);
    });
  }

  // ── Navigate to join page ─────────────────────────────────────────────

  function onJoin(invite: CollabInvite) {
    setOpen(false);
    // Mark as read optimistically
    setUnreadCount((c) => Math.max(0, c - (invite.readAt ? 0 : 1)));
    setInvites((prev) => prev.map((i) => i.id === invite.id ? { ...i, readAt: new Date().toISOString() } : i));
    markInviteRead(invite.id).catch(() => {});
    // Navigate to join page, passing invite context via router state
    navigate(`/collab/${invite.roomId}`, {
      state: { inviteId: invite.id, roomName: invite.roomName },
    });
  }

  function relTime(iso: string) {
    const diff = Date.now() - new Date(iso).getTime();
    const m = Math.floor(diff / 60_000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  }

  // ── Render ────────────────────────────────────────────────────────────

  return (
    <div className="position-relative">
      {/* Bell button */}
      <button
        ref={buttonRef}
        type="button"
        className="btn btn-sm position-relative d-flex align-items-center justify-content-center"
        style={{
          width: 36, height: 36, borderRadius: 8, padding: 0,
          borderColor: 'var(--aqua-border)',
          color: 'var(--aqua-text)',
          background: open ? 'var(--aqua-surface-muted)' : 'transparent',
        }}
        onClick={togglePopover}
        title="Notifications"
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
      >
        <span style={{ fontSize: '1rem' }}>🔔</span>
        {unreadCount > 0 && (
          <span
            className="position-absolute badge rounded-pill bg-danger"
            style={{
              top: -4, right: -4,
              fontSize: '0.6rem',
              minWidth: 16, height: 16,
              padding: '0 4px',
              lineHeight: '16px',
            }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Popover */}
      {open && (
        <div
          ref={popoverRef}
          className="shadow-lg"
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: 8,
            width: 340,
            maxHeight: 480,
            overflowY: 'auto',
            zIndex: 1050,
            background: 'var(--aqua-surface)',
            border: '1px solid var(--aqua-border)',
            borderRadius: 12,
          }}
        >
          {/* Header */}
          <div
            className="d-flex align-items-center justify-content-between px-3 pt-3 pb-2"
            style={{ borderBottom: '1px solid var(--aqua-border)' }}
          >
            <span className="fw-semibold small" style={{ color: 'var(--aqua-text-strong)' }}>
              Notifications
            </span>
            <button
              className="btn btn-link btn-sm p-0 text-decoration-none"
              style={{ color: 'var(--aqua-primary)', fontSize: '0.75rem' }}
              onClick={() => { setOpen(false); navigate('/notifications'); }}
            >
              View all
            </button>
          </div>

          {/* Content */}
          {loadingPopover ? (
            <div className="d-flex align-items-center justify-content-center py-4">
              <span className="spinner-border spinner-border-sm text-muted" />
            </div>
          ) : invites.length === 0 ? (
            <div className="py-4 text-center text-muted small">
              No notifications yet.
            </div>
          ) : (
            <ul className="list-unstyled mb-0">
              {invites.map((invite) => (
                <li
                  key={invite.id}
                  style={{
                    borderBottom: '1px solid var(--aqua-border)',
                    background: invite.readAt ? undefined : 'rgba(var(--bs-primary-rgb), 0.04)',
                  }}
                >
                  <div className="px-3 py-3">
                    <div className="d-flex align-items-start justify-content-between gap-2">
                      <div className="flex-grow-1">
                        {!invite.readAt && (
                          <span
                            className="d-inline-block rounded-circle me-1 flex-shrink-0"
                            style={{ width: 6, height: 6, background: 'var(--aqua-primary)', verticalAlign: 'middle' }}
                          />
                        )}
                        {invite.sessionId ? (
                          // meeting_invite — a session started
                          <>
                            <span className="small fw-semibold" style={{ color: 'var(--aqua-text-strong)' }}>
                              Meeting started
                            </span>
                            {invite.startedByName && (
                              <span className="small text-muted"> by {invite.startedByName}</span>
                            )}
                          </>
                        ) : (
                          // room_invite — added to a room, no live meeting yet
                          <>
                            <span className="small fw-semibold" style={{ color: 'var(--aqua-text-strong)' }}>
                              You were added
                            </span>
                            {invite.startedByName && (
                              <span className="small text-muted"> by {invite.startedByName}</span>
                            )}
                          </>
                        )}
                        <div className="small" style={{ color: 'var(--aqua-text)' }}>
                          {invite.sessionId ? 'in ' : 'to '}
                          <strong>{invite.roomName}</strong>
                        </div>
                        <div className="text-muted" style={{ fontSize: '0.7rem', marginTop: 2 }}>
                          {relTime(invite.createdAt)}
                        </div>
                      </div>
                      <div className="d-flex gap-1 flex-shrink-0 align-items-start">
                        <button
                          className="btn btn-primary btn-sm"
                          style={{ fontSize: '0.75rem', padding: '0.25rem 0.6rem', borderRadius: 6 }}
                          onClick={() => onJoin(invite)}
                          title={invite.sessionId ? 'Join meeting' : 'Open room'}
                        >
                          {invite.sessionId ? 'Join' : 'Open'}
                        </button>
                        <button
                          className="btn btn-sm p-0 d-flex align-items-center justify-content-center"
                          style={{
                            width: 22, height: 22, borderRadius: 4,
                            color: 'var(--aqua-text-muted)',
                            background: 'transparent', border: 'none',
                            fontSize: '1rem', lineHeight: 1,
                          }}
                          onClick={(ev) => onDismiss(invite, ev)}
                          title="Dismiss"
                          aria-label="Dismiss notification"
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
